import { useId, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import type { ZodError } from 'zod';

import { Field, IconButton, Input } from '@/components/ui';
import { cn } from '@/utils/cn';

import { useFieldContext } from './contexts';

const firstError = (errors: ZodError[]) => errors.find((e) => e !== undefined)?.message;

type AutocompleteProps<T> = {
  label?: string;
  placeholder?: string;
  helperText?: string;
  required?: boolean;
  options?: T[];
  freeSolo?: boolean;
  getOptionLabel?: (option: T | string) => string;
  isOptionEqualToValue?: (option: T, value: T) => boolean;
  /**
   * Accepts the union (string | T) so freeSolo callers can normalize raw user input
   * into the structured T type. Mirrors the previous MUI-based behavior.
   */
  transformValue?: (value: (string | T)[]) => T[];
  /**
   * Accepted but ignored — legacy MUI prop. Chips are rendered internally via getOptionLabel.
   * TODO: remove call-site usage during Phase 4 detail-section restyle.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  renderValue?: (value: any, getItemProps: (props: { index: number }) => any) => unknown;
};

export function Autocomplete<T>({
  label,
  placeholder,
  helperText,
  required,
  options = [],
  freeSolo = true,
  getOptionLabel = (o) => String(o),
  isOptionEqualToValue = (a, b) => a === b,
  transformValue,
}: AutocompleteProps<T>) {
  const field = useFieldContext<T[]>();
  const reactId = useId();
  const inputId = `ac-${reactId}`;
  const inputRef = useRef<HTMLInputElement>(null);
  const [draft, setDraft] = useState('');
  const [open, setOpen] = useState(false);

  const value = field.state.value ?? [];
  const errMsg = field.state.meta.isTouched ? firstError(field.state.meta.errors) : undefined;

  const filtered = useMemo(() => {
    const q = draft.trim().toLowerCase();
    if (!q) return options.slice(0, 8);
    return options
      .filter((o) => getOptionLabel(o).toLowerCase().includes(q))
      .slice(0, 8);
  }, [draft, options, getOptionLabel]);

  const commit = (next: (string | T)[]) => {
    const finalNext = transformValue ? transformValue(next) : (next as T[]);
    field.handleChange(finalNext);
  };

  const addItem = (item: T) => {
    if (value.some((v) => isOptionEqualToValue(item, v))) return;
    commit([...value, item]);
    setDraft('');
    setOpen(false);
    inputRef.current?.focus();
  };

  const addDraft = () => {
    const trimmed = draft.trim();
    if (!trimmed || !freeSolo) return;
    if (value.some((v) => getOptionLabel(v) === trimmed)) {
      setDraft('');
      return;
    }
    commit([...value, trimmed]);
    setDraft('');
    setOpen(false);
    inputRef.current?.focus();
  };

  const removeAt = (idx: number) => {
    commit(value.filter((_, i) => i !== idx));
  };

  const onKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if ((e.key === 'Enter' || e.key === ',') && draft) {
      e.preventDefault();
      addDraft();
    } else if (e.key === 'Backspace' && !draft && value.length > 0) {
      removeAt(value.length - 1);
    }
  };

  return (
    <Field label={label} required={required} error={errMsg} hint={!errMsg ? helperText : undefined} htmlFor={inputId}>
      <div
        className={cn('ds-input', errMsg && 'error')}
        style={{
          height: 'auto',
          minHeight: 34,
          padding: '4px 6px',
          display: 'flex',
          flexWrap: 'wrap',
          gap: 4,
          alignItems: 'center',
          cursor: 'text',
        }}
        onClick={() => inputRef.current?.focus()}
      >
        {value.map((v, i) => (
          <span key={i} className="ds-tag brand" style={{ height: 22, paddingRight: 4 }}>
            {getOptionLabel(v)}
            <IconButton
              icon="x"
              iconSize={10}
              size="sm"
              label="Remove"
              className="ds-icon-btn-sm"
              style={{ width: 16, height: 16, marginLeft: 2, color: 'currentColor' }}
              onClick={(e) => {
                e.stopPropagation();
                removeAt(i);
              }}
            />
          </span>
        ))}
        <input
          ref={inputRef}
          id={inputId}
          value={draft}
          placeholder={value.length === 0 ? placeholder : undefined}
          onChange={(e) => {
            setDraft(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => {
            field.handleBlur();
            // Defer so option-click can fire
            window.setTimeout(() => setOpen(false), 120);
          }}
          onKeyDown={onKey}
          style={{
            flex: 1,
            minWidth: 80,
            border: 'none',
            outline: 'none',
            background: 'transparent',
            fontSize: 13,
            color: 'inherit',
            height: 26,
          }}
        />
      </div>
      {open && filtered.length > 0 && (
        <div className="ds-popover" style={{ position: 'relative', marginTop: 4, padding: 4, top: 0, left: 0 }}>
          {filtered.map((opt, i) => (
            <button
              key={i}
              type="button"
              className="ds-menu-item"
              onMouseDown={(e) => {
                e.preventDefault();
                addItem(opt);
              }}
            >
              {getOptionLabel(opt)}
            </button>
          ))}
        </div>
      )}
    </Field>
  );
}
